import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useCreatePositionActivationItem,
  useDeletePositionActivationItem,
  usePositionActivationItems,
  useUpdatePositionActivationItem,
} from 'hooks/incident/usePositionActivationItems';

type PositionActivationChecklistProps = {
  positionId?: string;
  positionName?: string;
};

/**
 * The Activation tab of a roster seat: a checklist the user fills in for this
 * position, saved apart from the task list.
 */
const PositionActivationChecklist: React.FC<
  PositionActivationChecklistProps
> = ({ positionId, positionName }) => {
  const items = usePositionActivationItems(positionId);
  const createItem = useCreatePositionActivationItem(positionId ?? '');
  const updateItem = useUpdatePositionActivationItem(positionId ?? '');
  const deleteItem = useDeletePositionActivationItem(positionId ?? '');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState('');

  if (!positionId) {
    return (
      <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
        Save this position before adding activation steps.
      </p>
    );
  }

  const saveDraft = () => {
    const label = draft.trim();
    if (!label || createItem.isPending) return;
    createItem.mutate(label, {
      onSuccess: () => {
        setDraft('');
        setAdding(false);
      },
    });
  };

  const saveEdit = (itemId: string) => {
    const label = editingLabel.trim();
    if (!label) return;
    updateItem.mutate(
      { itemId, label },
      {
        onSuccess: () => setEditingId(null),
      }
    );
  };

  return (
    <section
      className="tw:flex tw:flex-col tw:gap-3"
      aria-label={
        positionName
          ? `Activation checklist for ${positionName}`
          : 'Activation checklist'
      }
    >
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <h3 className="tw:m-0 tw:text-sm tw:font-semibold">
          Activation Checklist
        </h3>
        <Button
          type="button"
          size="sm"
          disabled={adding}
          onClick={() => {
            setAdding(true);
            setDraft('');
          }}
        >
          <Plus />
          Add item
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="tw:w-10">
              <span className="tw:sr-only">Done</span>
            </TableHead>
            <TableHead>Item</TableHead>
            <TableHead className="tw:w-16">
              <span className="tw:sr-only">Remove</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {adding ? (
            <TableRow>
              <TableCell />
              <TableCell>
                <Input
                  autoFocus
                  value={draft}
                  placeholder="Activation step"
                  aria-label="New activation checklist item"
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      saveDraft();
                    }
                    if (event.key === 'Escape') setAdding(false);
                  }}
                />
              </TableCell>
              <TableCell>
                <Button
                  type="button"
                  size="sm"
                  disabled={!draft.trim() || createItem.isPending}
                  onClick={saveDraft}
                >
                  Save
                </Button>
              </TableCell>
            </TableRow>
          ) : null}
          {(items.data ?? []).length === 0 && !adding ? (
            <TableRow>
              <TableCell
                colSpan={3}
                className="tw:text-sm tw:text-muted-foreground"
              >
                No activation steps yet.
              </TableCell>
            </TableRow>
          ) : (
            (items.data ?? []).map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <Checkbox
                    checked={item.done}
                    aria-label={`Mark ${item.label} done`}
                    onCheckedChange={(checked) =>
                      updateItem.mutate({
                        itemId: item.id,
                        done: checked === true,
                      })
                    }
                  />
                </TableCell>
                <TableCell>
                  {editingId === item.id ? (
                    <Input
                      autoFocus
                      value={editingLabel}
                      aria-label={`Edit ${item.label}`}
                      onChange={(event) => setEditingLabel(event.target.value)}
                      onBlur={() => saveEdit(item.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          saveEdit(item.id);
                        }
                        if (event.key === 'Escape') setEditingId(null);
                      }}
                    />
                  ) : (
                    <button
                      type="button"
                      className={
                        item.done
                          ? 'tw:text-left tw:text-muted-foreground tw:line-through'
                          : 'tw:text-left'
                      }
                      onClick={() => {
                        setEditingId(item.id);
                        setEditingLabel(item.label);
                      }}
                    >
                      {item.label}
                    </button>
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove ${item.label}`}
                    onClick={() => deleteItem.mutate(item.id)}
                  >
                    <Trash2 />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </section>
  );
};

export default PositionActivationChecklist;
